import Image, { type StaticImageData } from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

import Card from "@/blueprint/components/Card/Card";
import Eyebrow from "@/blueprint/components/Eyebrow/Eyebrow";
import Text from "@/blueprint/components/Text/Text";
import { HOVER_IMAGE_ZOOM_CLASSES } from "@/blueprint/tokens/Motion";
import { cn } from "@/lib/utils";

/**
 * The cross-link banner both the shop's Behind the Design section and the
 * home page's Life with Matic section instantiate — Figma `9410:43237` and
 * `9410:43130`, one component set drawn twice. A full-bleed visual on a
 * rounded surface, with a white card holding an eyebrow, a title and an
 * arrow.
 *
 * The visual ships as four baked node exports because each breakpoint crops
 * the source differently (the transforms live in the Figma fills). The card
 * hugs its copy from tablet up and pins to the bottom-right; at mobile it
 * spans the banner's width, and its quadrant and layout are per-banner — the
 * dev annotation places it "in any quadrant to accommodate various visual
 * content", with text and icon alignment following the card. All the banner
 * steps land at 1280: Small Desktop shares the Desktop card wholesale, which
 * is why its radii and type ride banner tokens instead of the shared scales.
 */
export type CrossLinkBannerImages = {
  mobile: StaticImageData;
  tablet: StaticImageData;
  smallDesktop: StaticImageData;
  desktop: StaticImageData;
};

const BANNER_IMAGES = [
  { key: "mobile", visibility: "tab:hidden" },
  { key: "tablet", visibility: "hidden tab:block sd:hidden" },
  { key: "smallDesktop", visibility: "hidden sd:block d:hidden" },
  { key: "desktop", visibility: "hidden d:block" },
] as const;

type CrossLinkBannerProps = {
  href: string;
  onClick: () => void;
  eyebrow: string;
  /** Which banner eyebrow ramp the instance uses — Figma sizes them apart. */
  eyebrowSize: "sm" | "md";
  title: string;
  images: CrossLinkBannerImages;
  imageAlt: string;
  /**
   * The white card's mobile composition. Tablet up is always a bottom-right
   * row; at mobile Behind the Design parks a column at the top with the arrow
   * under the copy, Life with Matic a row at the bottom.
   */
  mobileCard: "top-column" | "bottom-row";
};

/**
 * The hover treatment, kept in one place because its two parts read as a
 * single gesture: the visual scales up behind a card box that does not move,
 * and the white panel's tint steps once. The arrow is not part of it — it
 * holds its ink and its position, as the node draws it.
 *
 * Both parts ride the motion family already here — `ease-out-strong`
 * throughout, `duration-quick` for the 200ms tint, and 300ms for the image,
 * which is a far larger thing to move and reads as sloppy if it keeps up with
 * the colour.
 *
 * The card box itself never transforms, pressed included: the blueprint's
 * press belongs to controls the size of a button, and the whole banner
 * flinching under the pointer is not the same gesture. So the link carries
 * only `group`, and the two parts below hang off hovering it.
 */
const HOVER_PANEL_CLASSES =
  "transition-colors duration-quick ease-out-strong group-hover:bg-brand-grey motion-reduce:transition-none";

const CrossLinkBanner = ({
  href,
  onClick,
  eyebrow,
  eyebrowSize,
  title,
  images,
  imageAlt,
  mobileCard,
}: CrossLinkBannerProps) => (
  <Link href={href} className="group block" onClick={onClick}>
    <Card
      radius="banner"
      className="relative h-[650px] w-full tab:h-[962px] sd:h-[648px] d:h-[900px]"
    >
      {BANNER_IMAGES.map(({ key, visibility }) => (
        <Image
          key={key}
          src={images[key]}
          alt={imageAlt}
          fill
          sizes="100vw"
          className={cn("object-cover", visibility, HOVER_IMAGE_ZOOM_CLASSES)}
        />
      ))}
      <div
        className={cn(
          "absolute z-10 rounded-banner-card bg-brand-white text-right",
          "py-xl pl-20 pr-20 tab:px-l tab:py-xxl sd:px-48 sd:py-4xl",
          "gap-s tab:gap-m sd:gap-l",
          "tab:bottom-xxl tab:left-auto tab:right-xxl tab:top-auto tab:flex-row tab:items-center",
          "sd:bottom-4xl sd:right-4xl",
          mobileCard === "top-column"
            ? "left-xl right-xl top-xl flex flex-col items-end"
            : "bottom-xl left-xl right-xl flex flex-row items-center",
          HOVER_PANEL_CLASSES,
        )}
      >
        <div className="flex w-full flex-col gap-s tab:w-auto tab:pl-60">
          <Eyebrow
            variant="eyebrow-sm"
            tone="primary"
            className={
              eyebrowSize === "sm"
                ? "text-banner-card-eyebrow-sm"
                : "text-banner-card-eyebrow-md"
            }
          >
            {eyebrow}
          </Eyebrow>
          <Text variant="h5" as="h2" className="text-banner-card-title">
            {title}
          </Text>
        </div>
        <span
          aria-hidden
          className="flex size-xl shrink-0 items-center justify-center"
        >
          <ArrowRight className="size-l text-content-primary" />
        </span>
      </div>
    </Card>
  </Link>
);

export default CrossLinkBanner;
